import type { ComponentType, SVGProps } from 'react'


/**
 * The PDApp mark — the product's symbol for an app, replacing the lucide `Box`
 * that stood in for it everywhere.
 *
 * DELIBERATELY NOT round-capped. Lucide's house style is
 * `stroke-linecap="round"` + `stroke-linejoin="round"`; this mark is drawn as
 * FOUR SEPARATE SEGMENTS with the SVG default flat (`butt`) caps, which is how
 * the source artwork is drawn and is the reason the corners carry a small spur
 * where a diagonal's flat end runs past the bar's. That notch is the mark's
 * character, not an artifact — do not "fix" it by closing the path or adding
 * round joins, both of which erase it.
 *
 * Geometry: the source is a bowtie on a 425.2 grid whose four corners are the
 * corners of a 176 x 247 rectangle centred at (212.6, 212.6). Scaled to the
 * 24-grid with the proportion preserved to within 1.3% — corners at
 * (5.5, 3), (18.5, 3), (5.5, 21), (18.5, 21), so the drawn size is 13 x 20
 * including the stroke. That is NARROWER than lucide `Box` (18 wide), which is
 * inherent to a tall bowtie and matches what lucide's own `hourglass` does.
 *
 * Renders at 9-46px across the app (the smallest are the App badges in
 * ShareLinksDialog / WhoHasAccessDialog). At the low end the corner spur is
 * sub-pixel and antialiases into a slight fuzz; if it reads as fraying rather
 * than as design, SHORTEN THE TWO HORIZONTAL BARS (e.g. `M7 3h10` / `M7 21h10`)
 * to open a real gap instead of thickening anything — the outer silhouette must
 * not move.
 *
 * API mirrors `LucideIcon` (`size` + `className` + `style` + SVG passthrough)
 * so it is drop-in at every call site the `Box` glyph occupied, including the
 * `FileIcon` registry.
 */
export interface PDAppIconProps extends Omit<SVGProps<SVGSVGElement>, 'size'> {
  /** Edge length in px (square viewport). Matches lucide's `size` prop. */
  size?: number | string
}

export function PDAppIcon({ size = 24, ...props }: PDAppIconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      {...props}
    >
      <path d="M5.5 3h13M5.5 21h13M5.5 21 18.5 3M18.5 21 5.5 3" />
    </svg>
  )
}

/**
 * Structural type for anything renderable where a lucide glyph goes.
 *
 * `LucideIcon` is a `ForwardRefExoticComponent`, so a plain function component
 * — which `PDAppIcon` is — is NOT assignable to it. Registries and menu-item
 * types that used to say `icon: LucideIcon` say this instead: every lucide icon
 * satisfies it, and so does a hand-drawn one.
 */
export type GlyphIcon = ComponentType<PDAppIconProps>
